import React, { useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Tilt } from 'react-tilt';
import Particles from '@tsparticles/react';
import { loadSlim } from '@tsparticles/slim';
import { 
  TestTubeDiagonal, 
  FileText, 
  Star, 
  ListOrdered, 
  Image,
  Link2,
  PenLine,
  Bot,
  Sparkles,
  Facebook,
  Users,
  MessageCircle,
  ArrowRight
} from 'lucide-react';

const HomePage = () => {
  const tools = [
    { title: "Create New Prompt", description: "Craft custom prompts in PromptLab.", icon: PenLine, path: "/prompts/new", color: "#E04C16" },
    { title: "Single Article", description: "Build detailed articles with ease.", icon: FileText, path: "/articles/single", color: "#3B82F6" },
    { title: "Bulk Generator", description: "Mass-produce quality articles fast.", icon: Bot, path: "/articles/standard", color: "#10B981" },
    { title: "Product Reviews", description: "Generate Amazon review roundups.", icon: Star, path: "/articles/reviews", color: "#F59E0B" },
    { title: "Listicle Writer", description: "Create engaging listicles.", icon: ListOrdered, path: "/articles/lists", color: "#8B5CF6" },
    { title: "URL Rewriter", description: "Rewrite content uniquely.", icon: Link2, path: "/url-rewriter", color: "#4F46E5" },
    { title: "Image Generator", description: "AI-crafted images for your work.", icon: Image, path: "/images", color: "#EC4899" },
    { title: "Saved Prompts", description: "Manage your prompt library.", icon: TestTubeDiagonal, path: "/prompts/saved", color: "#14B8A6" },
    { title: "FB Image/Meme Generator", description: "Eye-catching Facebook visuals.", icon: Image, path: "/social/facebook", color: "#2563EB" },
    { title: "Facebook Ideas", description: "Inspiration for FB posts.", icon: PenLine, path: "/social/facebookideas", color: "#EA580C" },
  ];

  const particlesInit = useCallback(async (engine) => {
    await loadSlim(engine);
  }, []);

  const particlesOptions = {
    particles: {
      number: { value: 20, density: { enable: true, value_area: 800 } },
      color: { value: "#E04C16" },
      shape: { type: "circle" },
      opacity: { value: 0.15, random: true },
      size: { value: 3, random: true },
      move: {
        enable: true,
        speed: 0.6,
        direction: "none",
        random: true,
        out_mode: "out",
        bounce: false,
      },
    },
    interactivity: {
      events: {
        onhover: { enable: true, mode: "repulse" },
        onclick: { enable: true, mode: "push" },
      },
      modes: {
        repulse: { distance: 150, duration: 0.4 },
        push: { particles_nb: 3 },
      },
    },
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 text-gray-800 overflow-hidden relative -m-6">
      <Particles
        id="tsparticles"
        init={particlesInit}
        options={particlesOptions}
        className="absolute inset-0 z-0"
      />

      {/* Hero Section */}
      <div className="relative z-10 pt-20 pb-12 text-center px-4">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-4xl md:text-5xl font-bold mb-6 bg-clip-text text-transparent bg-gradient-to-r from-[#E04C16] to-[#8B5CF6]">
            AI-Powered Content Creation
          </h1>
          <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed font-light">
            Your intelligent companion for creating exceptional content
          </p>
          <div className="mt-6 flex justify-center space-x-4">
            <Sparkles className="w-6 h-6 text-[#E04C16] animate-pulse" />
          </div>
        </div>
      </div>

      {/* Facebook Community Section - Smaller and more elegant */}
      <div className="relative z-10 max-w-4xl mx-auto px-6 pb-10">
        <div className="bg-white p-5 rounded-xl shadow-md border border-gray-100">
          <div className="grid md:grid-cols-5 gap-4 items-center">
            <div className="md:col-span-4 text-left space-y-3">
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 rounded-lg bg-[#1877F2] flex items-center justify-center shadow-sm">
                  <Facebook size={16} className="text-white" />
                </div>
                <h2 className="text-lg font-semibold text-gray-800">Join Our Facebook Community</h2>
              </div>
              <p className="text-gray-600 text-sm">Connect with fellow content creators and get expert tips and support.</p>
              <a
                href="https://www.facebook.com/groups/660096973209653"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center px-4 py-1.5 bg-[#1877F2] text-white rounded-md text-sm font-medium 
                  hover:bg-[#0c5bce] transition-all duration-300 space-x-1.5"
              >
                <span>Join Group</span>
                <ArrowRight size={14} />
              </a>
            </div>
            <div className="hidden md:flex md:col-span-1 justify-center">
              <Facebook size={40} className="text-[#1877F2]" />
            </div>
          </div>
        </div>
      </div>

      {/* Tools Grid - Improved design */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 pb-20">
        <h2 className="text-2xl font-semibold text-gray-800 mb-6 text-center">Content Creation Tools</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {tools.map((tool) => (
            <Tilt key={tool.path} options={{ max: 10, scale: 1.01, speed: 300 }}>
              <Link
                to={tool.path}
                className="block h-[180px] p-5 rounded-xl bg-white border border-gray-100
                  hover:shadow-md transition-all duration-300 flex flex-col"
                style={{ 
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.03)'
                }}
              >
                <div className="flex items-center space-x-3 mb-3">
                  <div className="p-1.5 rounded-md" style={{ backgroundColor: `${tool.color}15` }}>
                    <tool.icon size={18} style={{ color: tool.color }} />
                  </div>
                  <h3 className="text-base font-medium text-gray-800">{tool.title}</h3>
                </div>
                <p className="text-sm text-gray-500 flex-grow font-light">{tool.description}</p>
                <div className="flex justify-end mt-2">
                  <ArrowRight size={16} style={{ color: tool.color }} />
                </div>
              </Link>
            </Tilt>
          ))}
        </div>
      </div>
    </div>
  );
};

export default HomePage;